var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { nanoid } from 'nanoid';
import { computed, makeObservable } from 'mobx';
import { createTransformer } from 'mobx-utils';
import { AttachedTraitImport } from './modules/AttachedTraitImport';
import { memoize } from '../../../utils/mobx/decorators/memoize';
import { AttachedCss } from './modules/AttachedTraitCss';
import { AttachedTraitCondition } from './modules/AttachedTraitCondition';
import { AttachedTraitActive } from './modules/AttachedTraitActive';
import { AttachedScaledValue } from './modules/AttachedScaledValue';
import { AttachedContext } from '../../context/AttachedContext';
import { Actor } from '../../actor/Actor';
import { ActorStore } from '../../actor/modules/ActorStore';
import { AttachedTraitInfo } from './modules/AttachedTraitInfo';
import { registry, valueRegistry } from '../../../registry/Registry';
import { strip } from '../../../utils/serializr/schema/inner/strip';
import { runtime } from '../../runtime/Runtime';
import { mergeIntoAspectsTemplate } from '../attachedspecific/AttachedActionTrait';
import { bound } from '../../../utils/decorators/basic';
export class AttachedTrait extends Actor {
    type = 'attachedtrait';
    trait;
    constructor({ parent, trait }) {
        super({ parent });
        makeObservable(this);
        this.parent = parent;
        this.trait = trait;
    }
    get id() {
        return nanoid(6);
    }
    get actorStore() {
        return ActorStore.fromSerialized({ parent: this });
    }
    get readableIdentifier() {
        return `${this.type}_${this.id}`;
    }
    get aspect() {
        return this.parent;
    }
    get actor() {
        return this.aspect.actor;
    }
    get context() {
        return new AttachedContext(this);
    }
    get key() {
        return this.trait.key;
    }
    get traitValue() {
        return this.trait.value;
    }
    get reg() {
        return this.trait.reg;
    }
    get resolvedReg() {
        return new Proxy(this.reg, new RegResolveProxy(this));
    }
    get traitGroup() {
        return this.trait.traitGroup;
    }
    get attachedImport() {
        return new AttachedTraitImport(this);
    }
    get attachedCss() {
        return new AttachedCss(this);
    }
    get attachedCondition() {
        return new AttachedTraitCondition(this);
    }
    get attachedActive() {
        return new AttachedTraitActive(this);
    }
    get attachedTraitEdit() {
        return new runtime.solidPackages.Edit.classes.AttachedTraitEdit(this);
    }
    get attachedInfo() {
        return new AttachedTraitInfo(this);
    }
    get aspectsTemplate() {
        const aspectsTemplate = this.reg.aspectsTemplate || {};
        const fromMetaInfo = (runtime.availablePackages.Edit &&
            runtime.availablePackages.Edit.registryMetaInfo.traits[this.key] &&
            runtime.availablePackages.Edit.registryMetaInfo.traits[this.key].aspectsTemplate) ||
            {};
        mergeIntoAspectsTemplate(fromMetaInfo, aspectsTemplate);
        if (this.trait.hasValueFromRegistry) {
            const valueReg = registry.valueRegistry.registry_object[this.trait.value_stored];
            if (valueReg) {
                mergeIntoAspectsTemplate(valueReg.paramsFinal, aspectsTemplate);
            }
        }
        return aspectsTemplate;
    }
    get persistentActor() {
        return this.trait;
    }
    get attachedScaledValue() {
        return new AttachedScaledValue(this);
    }
    get scaledValue() {
        return this.attachedScaledValue.scaledValue;
    }
    get attachedValue() {
        if (typeof this.trait.value_stored === 'function') {
            // @ts-ignore
            return this.trait.value_stored(this);
        }
        if (this.trait.hasValueFromRegistry) {
            {
                const reg = valueRegistry.registry_object[this.trait.value_stored];
                if (reg) {
                    return reg.value(this.context);
                }
            }
        }
        return this.trait.value_stored;
    }
    get prio() {
        return this.attachedActive.prio;
    }
    get tags() {
        return [...this.autoTags, ...this.trait.tags]; // concat(this.trait.core.local.valuesOf.actorRoleName)
    }
    static get stripInfo() {
        return {
            extends: Actor.stripInfo,
            props: {
                // trait: strip((at: AttachedTrait) => at.trait.serialized),
                roles: strip((at) => at.trait.actorStore.tags),
            },
        };
    }
}
__decorate([
    memoize
], AttachedTrait.prototype, "id", null);
__decorate([
    memoize
], AttachedTrait.prototype, "actorStore", null);
__decorate([
    memoize
], AttachedTrait.prototype, "context", null);
__decorate([
    computed
], AttachedTrait.prototype, "reg", null);
__decorate([
    memoize
], AttachedTrait.prototype, "resolvedReg", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedImport", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedCss", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedCondition", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedActive", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedTraitEdit", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedInfo", null);
__decorate([
    memoize
], AttachedTrait.prototype, "attachedScaledValue", null);
__decorate([
    memoize
], AttachedTrait, "stripInfo", null);
export class RegResolveProxy {
    at;
    constructor(at) {
        this.at = at;
    }
    get(reg, prop) {
        return this.resolveProp(prop);
    }
    resolvePropFunction(prop) {
        const regAttr = (runtime.availablePackages.Edit &&
            runtime.availablePackages.Edit.registryMetaInfo.traits[this.at.key] &&
            typeof runtime.availablePackages.Edit.registryMetaInfo.traits[this.at.key][prop] !== 'undefined' &&
            runtime.availablePackages.Edit.registryMetaInfo.traits[this.at.key][prop]) ||
            this.at.reg[prop];
        if (['css', 'createTraits', 'className', 'stylesheet'].includes(prop) || typeof regAttr !== 'function') {
            return regAttr;
        }
        return regAttr(this.at.context);
    }
    get resolveProp() {
        return createTransformer(this.resolvePropFunction);
    }
}
__decorate([
    bound
], RegResolveProxy.prototype, "resolvePropFunction", null);
__decorate([
    memoize
], RegResolveProxy.prototype, "resolveProp", null);
